/**
 * iter236 · Day panel for the movement calendars.
 *
 * Outward: every event is a bill (the movement) plus the transport card, so the
 * only thing you edit here is logistics — transporter, vehicle, LR, tracking.
 * Saving never touches stock again.
 * Inward: arrivals (GRN), money-only vendor bills and expected POs, read-only.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Truck, PackageCheck, IndianRupee, CalendarClock, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const money = (n) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0,
}).format(n || 0);

export const STAGE_TONE = {
  billed_not_loaded: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  planned: 'bg-zinc-800 text-zinc-300 border-zinc-700',
  packed: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  in_transit: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
  delivered: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  unbilled: 'bg-red-500/15 text-red-300 border-red-500/40',
  arrived: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  billed_only: 'bg-zinc-800 text-zinc-400 border-zinc-700',
  expected: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  overdue: 'bg-red-500/15 text-red-300 border-red-500/40',
  received: 'bg-emerald-500/10 text-emerald-400/70 border-emerald-500/25',
};

const FLAG_TONE = {
  danger: 'text-red-300',
  warn: 'text-amber-300',
  info: 'text-zinc-400',
};

const inputCls = 'w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-[12px] text-zinc-100 focus:outline-none focus:border-amber-500';

export default function MovementDayPanel({ day, direction, transporters = [], onClose, onSaved }) {
  const [editing, setEditing] = useState(null);
  if (!day) return null;
  const accent = direction === 'outward' ? 'text-emerald-400' : 'text-amber-400';

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="mv-day-panel">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-[520px] h-full bg-zinc-950 border-l border-zinc-800 overflow-y-auto">
        <div className="sticky top-0 bg-zinc-950/95 backdrop-blur border-b border-zinc-800 px-4 py-3 flex items-center gap-3">
          <div className="flex-1">
            <p className="font-heading text-sm text-zinc-50">
              {day.weekday} · {day.date}
            </p>
            <p className="text-[11px] text-zinc-500">
              {direction === 'outward' ? 'Goods out' : 'Goods in'} · {day.kg.toLocaleString()} kg
              {day.amount ? ` · ${money(day.amount)}` : ''}
              {day.money_amount ? ` · bills ${money(day.money_amount)}` : ''}
            </p>
          </div>
          <button onClick={onClose} data-testid="mv-day-close" className="text-zinc-500 hover:text-zinc-200">
            <X size={17} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {day.events.length === 0 && (
            <p className="text-[12px] text-zinc-600">Nothing moved on this day.</p>
          )}
          {day.events.map(e => (
            <div key={e.id} data-testid={`mv-event-${e.id}`}
              className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-3 space-y-2">
              <div className="flex items-start gap-2 flex-wrap">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${STAGE_TONE[e.stage] || STAGE_TONE.planned}`}>
                  {e.stage_label}
                </span>
                <span className="text-[10px] text-zinc-500">{e.entity_label}</span>
                <span className="flex-1" />
                <span className={`text-[12px] font-mono ${accent}`}>
                  {e.qty_kg ? `${e.qty_kg.toLocaleString()} kg` : (e.billed_kg ? `${e.billed_kg} kg billed` : (e.expected_kg ? `${e.expected_kg} kg expected` : '—'))}
                </span>
              </div>

              <div>
                <p className="text-[13px] text-zinc-100">{e.party || '—'}</p>
                <p className="text-[11px] text-zinc-500 font-mono">
                  {e.doc_no}{e.second_doc_no ? ` · ${e.second_doc_no}` : ''}
                  {e.amount ? ` · ${money(e.amount)}` : ''}
                  {e.payment_status ? ` · ${e.payment_status}` : ''}
                </p>
                {e.bill_date && e.move_date && e.bill_date !== e.move_date && (
                  <p className="text-[10px] text-zinc-500">billed {e.bill_date} · loaded {e.move_date}</p>
                )}
              </div>

              {(e.items || []).length > 0 && (
                <div className="text-[11px] text-zinc-400 space-y-0.5">
                  {e.items.slice(0, 5).map((it, i) => (
                    <p key={i}>{it.product_name || '—'} — {(it.qty_kg ?? it.qty)?.toLocaleString?.() ?? it.qty} kg</p>
                  ))}
                  {e.items.length > 5 && <p className="text-zinc-600">+{e.items.length - 5} more line(s)</p>}
                </div>
              )}

              {(e.flags || []).map((f, i) => (
                <p key={i} className={`text-[11px] ${FLAG_TONE[f.tone] || 'text-zinc-400'}`}>• {f.label}</p>
              ))}

              {direction === 'outward' && (
                <OutwardLogistics event={e} transporters={transporters}
                  editing={editing === e.id} onEdit={() => setEditing(editing === e.id ? null : e.id)}
                  onSaved={() => { setEditing(null); onSaved?.(); }} />
              )}

              {direction === 'inward' && (e.lots || []).length > 0 && (
                <p className="text-[10px] text-zinc-500">
                  Lots: {e.lots.map(l => l.lot_no).filter(Boolean).join(', ')}
                </p>
              )}
              {direction === 'inward' && e.layer === 'expected' && (
                <p className="text-[10px] text-zinc-500 flex items-center gap-1">
                  <CalendarClock size={11} /> {e.days_left >= 0 ? `${e.days_left} day(s) away` : `${Math.abs(e.days_left)} day(s) late`}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OutwardLogistics({ event, transporters, editing, onEdit, onSaved }) {
  const l = event.logistics || {};
  const [f, setF] = useState({
    dispatch_date: event.move_date || event.bill_date || '',
    status: l.status || 'Planned',
    transporter_name: l.transporter_name || '',
    vehicle_no: l.vehicle_no || '',
    lr_no: l.lr_no || '',
    tracking_link: l.tracking_link || '',
    eway_bill_no: l.eway_bill_no || '',
    dispatch_mode: l.mode || 'transport',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const orderId = event.links?.sales_order_id;

  const save = async () => {
    if (!orderId) { toast.error('This movement has no bill to attach transport to'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/movements/outward/${orderId}/logistics`, f, { withCredentials: true });
      toast.success('Transport saved · stock untouched (the bill already moved it)');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save transport');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!orderId) return;
    if (!window.confirm('Remove the transport details from this bill?')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/movements/outward/${orderId}/logistics`, { withCredentials: true });
      toast.success('Transport removed · stock untouched');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove transport');
    } finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-zinc-800/70">
        <span className="text-[11px] text-zinc-400 flex items-center gap-1">
          <Truck size={11} />
          {l.has_card
            ? [l.transporter_name, l.vehicle_no, l.lr_no && `LR ${l.lr_no}`].filter(Boolean).join(' · ') || 'Card created, no transport details'
            : 'No transport details yet'}
        </span>
        <span className="flex-1" />
        {l.tracking_link && (
          <a href={l.tracking_link} target="_blank" rel="noreferrer"
            className="text-[10px] text-sky-300 hover:text-sky-200 flex items-center gap-1">
            track <ExternalLink size={10} />
          </a>
        )}
        {orderId && (
          <button onClick={onEdit} data-testid={`mv-transport-open-${event.id}`}
            className="text-[11px] text-amber-300 hover:text-amber-200 border border-amber-500/30 rounded-lg px-2 py-1">
            {l.has_card ? 'Edit transport' : 'Add transport'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="pt-2 border-t border-zinc-800/70 space-y-2" data-testid={`mv-transport-form-${event.id}`}>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Loaded on</span>
          <input type="date" value={f.dispatch_date} onChange={e => set('dispatch_date', e.target.value)}
            data-testid="mv-transport-loaded-date" className={inputCls} />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Status</span>
          <select value={f.status} onChange={e => set('status', e.target.value)}
            data-testid="mv-transport-status" className={inputCls}>
            {['Planned', 'Packed', 'In Transit', 'Delivered', 'Received'].map(s => (
              <option key={s} value={s} className="bg-zinc-900">{s}</option>
            ))}
          </select>
        </label>
        <label className="block col-span-2">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Transporter</span>
          <input list="mv-transporters" value={f.transporter_name} onChange={e => set('transporter_name', e.target.value)}
            data-testid="mv-transport-transporter" className={inputCls} placeholder="e.g. VRL Logistics" />
          <datalist id="mv-transporters">
            {transporters.map(t => <option key={t.id || t.name} value={t.name} />)}
          </datalist>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Vehicle no.</span>
          <input value={f.vehicle_no} onChange={e => set('vehicle_no', e.target.value)}
            data-testid="mv-transport-vehicle" className={inputCls} placeholder="GJ-05-XX-0000" />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">LR / docket no.</span>
          <input value={f.lr_no} onChange={e => set('lr_no', e.target.value)}
            data-testid="mv-transport-lr" className={inputCls} />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">E-way bill</span>
          <input value={f.eway_bill_no} onChange={e => set('eway_bill_no', e.target.value)}
            data-testid="mv-transport-eway" className={inputCls} />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Tracking link</span>
          <input value={f.tracking_link} onChange={e => set('tracking_link', e.target.value)}
            data-testid="mv-transport-tracking" className={inputCls} />
        </label>
      </div>
      <p className="text-[10px] text-zinc-600 flex items-center gap-1">
        <PackageCheck size={11} /> The bill already took this stock out — saving transport never deducts it again.
      </p>
      <div className="flex items-center justify-end gap-2">
        {l.has_card && (
          <button onClick={remove} disabled={saving} data-testid={`mv-transport-remove-${event.id}`}
            className="text-[11px] text-red-400 hover:text-red-300 mr-auto">Remove transport</button>
        )}
        <button onClick={onEdit} className="text-[11px] text-zinc-500 hover:text-zinc-300 px-2">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="mv-transport-save"
          className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-[11px] font-semibold px-3 py-1.5 rounded-lg">
          {saving ? 'Saving…' : 'Save transport'}
        </button>
      </div>
    </div>
  );
}

export { money, IndianRupee };
